// Profile API (spec §2, §13): last login, photo, service requests, nominees,
// settings, change mPIN and password, de-register.
import type {
  Nominee,
  ProfileInfo,
  ProfileSettings,
  ServiceRequest,
  ServiceRequestKind,
} from '@/types/phase2.types';
import { ApiError, del, get, patch, post, put } from './client';
import { MOCK_PROFILE, REAL_API_ENABLED } from './flags';
import { loadDb, saveDb } from '@/mock/db';
import type { AppNotification } from '@/types/phase4.types';
import * as mock from '@/mock/handlers/profile';
import type { ProfileSeed } from '@/mock/handlers/profile';

// A rejected promise, not a throw: callers use .catch(), and a synchronous throw
// inside a screen's effect blanks the whole page.
const notAvailable = <T = never>(): Promise<T> =>
  Promise.reject(new ApiError('NOT_AVAILABLE', 'The profile API is not available yet.'));

export type { ProfileSeed };

export interface CustomerPersonalInfo {
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  dateOfBirth: string;
  gender: string;
  maritalStatus: string;
  nationality: string;
  residentialStatus: string;
  countryOfBirth: string;
  motherName: string;
  fatherName: string;
  spouseName: string;
  occupationCategory: string;
  educationQualification: string;
  customerCategory: string;
  seniorCitizen: boolean;
  minor: boolean;
  politicallyExposedPerson: boolean;
}

export interface CustomerContact {
  primaryMobile: string;
  alternateMobile: string;
  primaryEmail: string;
  alternateEmail: string;
  preferredCommunicationMode: string;
  preferredLanguage: string;
  emailVerified: boolean;
  mobileVerified: boolean;
}

export type CustomerContactInput = Pick<
  CustomerContact,
  'alternateMobile' | 'alternateEmail' | 'preferredCommunicationMode' | 'preferredLanguage'
>;

export interface CustomerStatus {
  id: string;
  status: string;
  statusReason: string;
  deactivatedAt: string;
  reactivatedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerRiskProfile {
  id: string;
  riskCategory: string;
  riskScore: number;
  assessedAt: string;
  assessedBy: string;
  nextAssessmentDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerProfile {
  id: string;
  customerId: string;
  profileCompletionPercentage: number;
  onboardingStatus: string;
  personalInfo: CustomerPersonalInfo;
  contact: CustomerContact;
  status: CustomerStatus;
  riskProfile: CustomerRiskProfile;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerProfileUpdate {
  personalInfo: CustomerPersonalInfo & {
    panNumber?: string;
    aadhaarNumber?: string;
  };
  contact: Pick<
    CustomerContact,
    'alternateMobile' | 'alternateEmail' | 'preferredCommunicationMode' | 'preferredLanguage'
  >;
}

export interface CustomerProfileCreate {
  personalInfo: {
    dateOfBirth: string;
    gender: string;
    maritalStatus: string;
    nationality: string;
    residentialStatus: string;
    countryOfBirth: string;
    motherName: string;
    fatherName: string;
    spouseName: string;
    occupationCategory: string;
    educationQualification: string;
    customerCategory: string;
    seniorCitizen: boolean;
    minor: boolean;
    politicallyExposedPerson: boolean;
    panNumber: string;
    aadhaarNumber: string;
  };
  contact: {
    alternateMobile: string;
    alternateEmail: string;
    preferredCommunicationMode: string;
    preferredLanguage: string;
  };
}

export interface CustomerSummary {
  id: string;
  customerId: string;
  displayName: string;
  primaryMobile: string;
  primaryEmail: string;
  profileCompletionPercentage: number;
  onboardingStatus: string;
}

const customerProfilePath = (customerId: string): string =>
  `/customers/${encodeURIComponent(customerId)}`;

export const createCustomerProfile = (
  input: CustomerProfileCreate,
): Promise<CustomerProfile> =>
  REAL_API_ENABLED
    ? post<CustomerProfile>('/customers', input)
    : notAvailable();

export const getCustomerProfile = (customerId: string): Promise<CustomerProfile> =>
  REAL_API_ENABLED
    ? get<CustomerProfile>(customerProfilePath(customerId))
    : notAvailable();

export const getCurrentCustomerProfile = (): Promise<CustomerProfile> =>
  REAL_API_ENABLED
    ? get<CustomerProfile>('/customers/me')
    : notAvailable();

export const deactivateCustomerProfile = (
  customerId: string,
  reason: string,
): Promise<void> =>
  REAL_API_ENABLED
    ? post<void>(customerProfilePath(customerId) + '/deactivate', undefined, {
        params: { reason },
      })
    : notAvailable();

export const updateCustomerProfile = (
  customerId: string,
  input: CustomerProfileUpdate,
): Promise<CustomerProfile> =>
  REAL_API_ENABLED
    ? put<CustomerProfile>(customerProfilePath(customerId), input)
    : notAvailable();

export const updateCustomerContact = (
  customerId: string,
  input: CustomerContactInput,
): Promise<CustomerContact> =>
  REAL_API_ENABLED
    ? put<CustomerContact>(customerProfilePath(customerId) + '/contact', input)
    : notAvailable();

export const listCustomerSummaries = (): Promise<CustomerSummary[]> =>
  REAL_API_ENABLED
    ? get<CustomerSummary[]>('/customers')
    : notAvailable();

export interface AdminAccount {
  id: number;
  accountNumber: string;
  accountType: string;
  branch: string;
  balance: number;
  currency: string;
  status: string;
  updatedAt: string;
}

export interface AdminAccountInput {
  customerId: string;
  accountNumber: string;
  accountType: string;
  branch: string;
  balance: number;
  currency: string;
  status: string;
}

const adminAccountsPath = '/admin/accounts';

export const listAdminAccounts = (): Promise<AdminAccount[]> =>
  REAL_API_ENABLED
    ? get<AdminAccount[]>(adminAccountsPath)
    : notAvailable();

export const getAdminAccount = (id: number): Promise<AdminAccount> =>
  REAL_API_ENABLED
    ? get<AdminAccount>(`${adminAccountsPath}/${encodeURIComponent(id)}`)
    : notAvailable();

export const createAdminAccount = (input: AdminAccountInput): Promise<AdminAccount> =>
  REAL_API_ENABLED
    ? post<AdminAccount>(adminAccountsPath, input)
    : notAvailable();

export const updateAdminAccount = (
  id: number,
  input: AdminAccountInput,
): Promise<AdminAccount> =>
  REAL_API_ENABLED
    ? put<AdminAccount>(`${adminAccountsPath}/${encodeURIComponent(id)}`, input)
    : notAvailable();

export const closeAdminAccount = (id: number): Promise<AdminAccount> =>
  REAL_API_ENABLED
    ? del<AdminAccount>(`${adminAccountsPath}/${encodeURIComponent(id)}`)
    : notAvailable();

export interface CustomerKyc {
  id: string;
  kycStatus: string;
  kycLevel: string;
  kycExpiry: string;
  verificationDate: string;
  verifiedBy: string;
  verificationMethod: string;
  digilockerReference: string;
  ckycNumber: string;
  videoKycStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerKycInput {
  aadhaarNumber: string;
  panNumber: string;
  kycDate: string;
}

export interface GovernmentId {
  id: string;
  idType: string;
  maskedIdNumber: string;
  verificationStatus: string;
  verifiedAt: string;
  createdAt: string;
  updatedAt: string;
}

const customerKycPath = (customerId: string): string =>
  `/customers/${encodeURIComponent(customerId)}/kyc`;

export const getCustomerKyc = (customerId: string): Promise<CustomerKyc> =>
  REAL_API_ENABLED
    ? get<CustomerKyc>(customerKycPath(customerId))
    : notAvailable();

export const submitCustomerKyc = (
  customerId: string,
  input: CustomerKycInput,
): Promise<CustomerKyc> =>
  REAL_API_ENABLED
    ? put<CustomerKyc>(customerKycPath(customerId), input)
    : notAvailable();

export const listGovernmentIds = (customerId: string): Promise<GovernmentId[]> =>
  REAL_API_ENABLED
    ? get<GovernmentId[]>(`${customerKycPath(customerId)}/government-ids`)
    : notAvailable();

export interface CustomerDocument {
  id: string;
  documentType: string;
  maskedDocumentNumber: string;
  verificationStatus: string;
  issueDate: string;
  expiryDate: string;
  storageReference: string;
  checksum: string;
  uploadedBy: string;
  uploadedDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerDocumentMetadata {
  documentType: string;
  documentNumber: string;
  issueDate?: string;
  expiryDate?: string;
  storageReference?: string;
}

const customerDocumentsPath = (customerId: string): string =>
  `/customers/${encodeURIComponent(customerId)}/documents`;

export const listCustomerDocuments = (customerId: string): Promise<CustomerDocument[]> =>
  REAL_API_ENABLED
    ? get<CustomerDocument[]>(customerDocumentsPath(customerId))
    : notAvailable();

export const getCustomerDocument = (
  customerId: string,
  documentId: string,
): Promise<CustomerDocument> =>
  REAL_API_ENABLED
    ? get<CustomerDocument>(
        `${customerDocumentsPath(customerId)}/${encodeURIComponent(documentId)}`
      )
    : notAvailable();

export const uploadCustomerDocument = (
  customerId: string,
  metadata: CustomerDocumentMetadata,
  file?: File | Blob,
): Promise<CustomerDocument> => {
  if (!REAL_API_ENABLED) return notAvailable();

  const formData = new FormData();
  formData.append(
    'metadata',
    new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
  );
  if (file) formData.append('file', file);
  return post<CustomerDocument>(customerDocumentsPath(customerId), formData);
};

export const updateCustomerDocument = (
  customerId: string,
  documentId: string,
  metadata: CustomerDocumentMetadata,
): Promise<CustomerDocument> =>
  REAL_API_ENABLED
    ? put<CustomerDocument>(
        `${customerDocumentsPath(customerId)}/${encodeURIComponent(documentId)}`,
        metadata,
      )
    : notAvailable();

export const deleteCustomerDocument = (
  customerId: string,
  documentId: string,
): Promise<void> =>
  REAL_API_ENABLED
    ? del<void>(
        `${customerDocumentsPath(customerId)}/${encodeURIComponent(documentId)}`,
      )
    : notAvailable();

export interface CustomerPreferences {
  id: string;
  preferredLanguage: string;
  statementPreference: string;
  smsAlerts: boolean;
  emailAlerts: boolean;
  pushNotifications: boolean;
  marketingConsent: boolean;
  paperStatement: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CustomerPreferencesInput = Omit<
  CustomerPreferences,
  'id' | 'createdAt' | 'updatedAt'
>;

const customerPreferencesPath = (customerId: string): string =>
  `/customers/${encodeURIComponent(customerId)}/preferences`;

export const getCustomerPreferences = (
  customerId: string,
): Promise<CustomerPreferences> =>
  REAL_API_ENABLED
    ? get<CustomerPreferences>(customerPreferencesPath(customerId))
    : notAvailable();

export const updateCustomerPreferences = (
  customerId: string,
  input: CustomerPreferencesInput,
): Promise<CustomerPreferences> =>
  REAL_API_ENABLED
    ? put<CustomerPreferences>(customerPreferencesPath(customerId), input)
    : notAvailable();

export type CustomerConsentType =
  | 'TERMS_AND_CONDITIONS'
  | 'PRIVACY_POLICY'
  | 'MARKETING_COMMUNICATION'
  | 'DATA_SHARING'
  | 'KYC_VERIFICATION'
  | 'E_STATEMENT'
  | 'NOMINEE_CONSENT'
  | 'BIOMETRIC_CONSENT'
  | 'COMMUNICATION_PREFERENCE'
  | 'THIRD_PARTY_SHARING';

export interface CustomerConsent {
  type: string;
  accepted: boolean;
  recordedAt: string;
  documentVersion: string;
}

export interface GrantCustomerConsentInput {
  purpose: string;
  channel: string;
  consentVersion: string;
}

const customerConsentsPath = (customerId: string): string =>
  `/customers/${encodeURIComponent(customerId)}/consents`;

export const grantCustomerConsent = (
  customerId: string,
  consentType: CustomerConsentType,
  input: GrantCustomerConsentInput,
): Promise<CustomerConsent> =>
  REAL_API_ENABLED
    ? post<CustomerConsent>(
        `${customerConsentsPath(customerId)}/${encodeURIComponent(consentType)}`,
        undefined,
        { params: input },
      )
    : notAvailable();

export const withdrawCustomerConsent = (
  customerId: string,
  consentType: CustomerConsentType,
): Promise<CustomerConsent> =>
  REAL_API_ENABLED
    ? del<CustomerConsent>(
        `${customerConsentsPath(customerId)}/${encodeURIComponent(consentType)}`,
      )
    : notAvailable();

export const getCustomerConsents = (customerId: string): Promise<CustomerConsent[]> =>
  REAL_API_ENABLED
    ? get<CustomerConsent[]>(customerConsentsPath(customerId))
    : notAvailable();

export interface CustomerEmployment {
  id: string;
  occupationType: string;
  employerName: string;
  organizationName: string;
  designation: string;
  department: string;
  officeEmail: string;
  officePhone: string;
  yearsOfExperience: number;
  joiningDate: string;
  industry: string;
  employmentStatus: string;
  createdAt: string;
  updatedAt: string;
}

export type CustomerEmploymentInput = Omit<
  CustomerEmployment,
  'id' | 'createdAt' | 'updatedAt'
>;

const customerEmploymentPath = (customerId: string): string =>
  `/customers/${encodeURIComponent(customerId)}/employment`;

export const getCustomerEmployment = (customerId: string): Promise<CustomerEmployment> =>
  REAL_API_ENABLED
    ? get<CustomerEmployment>(customerEmploymentPath(customerId))
    : notAvailable();

export const updateCustomerEmployment = (
  customerId: string,
  input: CustomerEmploymentInput,
): Promise<CustomerEmployment> =>
  REAL_API_ENABLED
    ? put<CustomerEmployment>(customerEmploymentPath(customerId), input)
    : notAvailable();

export interface CustomerIncome {
  id: string;
  annualIncome: number;
  monthlyIncome: number;
  incomeRange: string;
  sourceOfIncome: string;
  taxResidency: string;
  incomeVerificationStatus: string;
  createdAt: string;
  updatedAt: string;
}

export type CustomerIncomeInput = Pick<
  CustomerIncome,
  'annualIncome' | 'monthlyIncome' | 'incomeRange' | 'sourceOfIncome' | 'taxResidency'
>;

const customerIncomePath = (customerId: string): string =>
  `/customers/${encodeURIComponent(customerId)}/income`;

export const getCustomerIncome = (customerId: string): Promise<CustomerIncome> =>
  REAL_API_ENABLED
    ? get<CustomerIncome>(customerIncomePath(customerId))
    : notAvailable();

export const updateCustomerIncome = (
  customerId: string,
  input: CustomerIncomeInput,
): Promise<CustomerIncome> =>
  REAL_API_ENABLED
    ? put<CustomerIncome>(customerIncomePath(customerId), input)
    : notAvailable();

export interface CustomerAddress {
  id: string;
  addressType: string;
  houseNumber: string;
  building: string;
  street: string;
  area: string;
  landmark: string;
  village: string;
  locality: string;
  city: string;
  district: string;
  state: string;
  country: string;
  pinCode: string;
  addressProofType: string;
  addressVerificationStatus: string;
  communicationAddress: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CustomerAddressInput = Omit<
  CustomerAddress,
  'id' | 'addressVerificationStatus' | 'createdAt' | 'updatedAt'
>;

const customerAddressesPath = (customerId: string): string =>
  `/customers/${encodeURIComponent(customerId)}/addresses`;

export const listCustomerAddresses = (customerId: string): Promise<CustomerAddress[]> =>
  REAL_API_ENABLED
    ? get<CustomerAddress[]>(customerAddressesPath(customerId))
    : notAvailable();

export const getCustomerAddress = (
  customerId: string,
  addressId: string,
): Promise<CustomerAddress> =>
  REAL_API_ENABLED
    ? get<CustomerAddress>(
        `${customerAddressesPath(customerId)}/${encodeURIComponent(addressId)}`
      )
    : notAvailable();

export const addCustomerAddress = (
  customerId: string,
  input: CustomerAddressInput,
): Promise<CustomerAddress> =>
  REAL_API_ENABLED
    ? post<CustomerAddress>(customerAddressesPath(customerId), input)
    : notAvailable();

export const updateCustomerAddress = (
  customerId: string,
  addressId: string,
  input: CustomerAddressInput,
): Promise<CustomerAddress> =>
  REAL_API_ENABLED
    ? put<CustomerAddress>(
        `${customerAddressesPath(customerId)}/${encodeURIComponent(addressId)}`,
        input
      )
    : notAvailable();

export const deleteCustomerAddress = (
  customerId: string,
  addressId: string,
): Promise<void> =>
  REAL_API_ENABLED
    ? del<void>(
        `${customerAddressesPath(customerId)}/${encodeURIComponent(addressId)}`
      )
    : notAvailable();

export interface EmergencyContact {
  id: string;
  name: string;
  relationship: string;
  mobileNumber: string;
  email?: string;
  address?: string;
  primary: boolean;
  createdAt: string;
  updatedAt: string;
}

export type EmergencyContactInput = Omit<
  EmergencyContact,
  'id' | 'createdAt' | 'updatedAt'
>;

const emergencyContactsPath = (customerId: string): string =>
  `/customers/${encodeURIComponent(customerId)}/emergency-contacts`;

export const listEmergencyContacts = (customerId: string): Promise<EmergencyContact[]> =>
  REAL_API_ENABLED
    ? get<EmergencyContact[]>(emergencyContactsPath(customerId))
    : notAvailable();

export const getEmergencyContact = (
  customerId: string,
  contactId: string,
): Promise<EmergencyContact> =>
  REAL_API_ENABLED
    ? get<EmergencyContact>(`${emergencyContactsPath(customerId)}/${encodeURIComponent(contactId)}`)
    : notAvailable();

export const addEmergencyContact = (
  customerId: string,
  input: EmergencyContactInput,
): Promise<EmergencyContact> =>
  REAL_API_ENABLED
    ? post<EmergencyContact>(emergencyContactsPath(customerId), input)
    : notAvailable();

export const updateEmergencyContact = (
  customerId: string,
  contactId: string,
  input: EmergencyContactInput,
): Promise<EmergencyContact> =>
  REAL_API_ENABLED
    ? put<EmergencyContact>(`${emergencyContactsPath(customerId)}/${encodeURIComponent(contactId)}`, input)
    : notAvailable();

export const deleteEmergencyContact = (
  customerId: string,
  contactId: string,
): Promise<void> =>
  REAL_API_ENABLED
    ? del<void>(`${emergencyContactsPath(customerId)}/${encodeURIComponent(contactId)}`)
    : notAvailable();

export interface CustomerNotification {
  id: string | number;
  title: string;
  body: string;
  type?: string;
  read: boolean;
  createdAt: string;
}

export interface NotificationPage {
  content: CustomerNotification[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  unreadCount: number;
}

export interface MockOutboxMessage {
  id: string;
  channel: string;
  recipient: string;
  maskedRecipient: string;
  subject: string;
  body: string;
  category: string;
  reference: string;
  createdAt: string;
}

export interface MockOutboxPage {
  totalPages: number;
  totalElements: number;
  first: boolean;
  last: boolean;
  pageable: Record<string, unknown>;
  numberOfElements: number;
  size: number;
  content: MockOutboxMessage[];
  number: number;
  sort: Record<string, unknown>;
  empty: boolean;
}

export const listMockOutbox = async (input: {
  reference?: string;
  recipient?: string;
  page?: number;
  size?: number;
  sort?: string[];
} = {}): Promise<MockOutboxPage> => {
  if (!REAL_API_ENABLED) return notAvailable();
  return get<MockOutboxPage>('/notifications/mock-outbox', {
    params: {
      reference: input.reference,
      recipient: input.recipient,
      page: input.page ?? 0,
      size: input.size ?? 20,
      sort: input.sort,
    },
  });
};

const toNotification = (notification: AppNotification): CustomerNotification => ({
  id: notification.id,
  title: notification.title,
  body: notification.body,
  type: 'ACCOUNT',
  read: notification.read,
  createdAt: new Date(notification.at).toISOString(),
});

export const listNotifications = async (input: {
  unread?: boolean;
  page?: number;
  size?: number;
} = {}): Promise<NotificationPage> => {
  const page = input.page ?? 0;
  const size = input.size ?? 20;
  if (REAL_API_ENABLED) {
    return get<NotificationPage>('/notifications', { params: { unread: input.unread ?? false, page, size } });
  }
  const all = loadDb().phase4.notifications.map(toNotification);
  const filtered = input.unread ? all.filter((notification) => !notification.read) : all;
  const start = page * size;
  return {
    content: filtered.slice(start, start + size),
    number: page,
    size,
    totalElements: filtered.length,
    totalPages: Math.max(1, Math.ceil(filtered.length / size)),
    first: page === 0,
    last: start + size >= filtered.length,
    unreadCount: all.filter((notification) => !notification.read).length,
  };
};

export const getUnreadNotificationCount = async (): Promise<number> => {
  if (REAL_API_ENABLED) {
    const data = await get<Record<string, unknown>>('/notifications/unread-count');
    return Number(data.unreadCount ?? data.count ?? data.total ?? 0);
  }
  return loadDb().phase4.notifications.filter((notification) => !notification.read).length;
};

export const markNotificationRead = async (id: string | number): Promise<CustomerNotification | void> => {
  if (REAL_API_ENABLED) return patch<CustomerNotification>(`/notifications/${encodeURIComponent(String(id))}/read`);
  const db = loadDb();
  const notification = db.phase4.notifications.find((item) => item.id === String(id));
  if (notification) notification.read = true;
  saveDb(db);
  return notification ? toNotification(notification) : undefined;
};

export const markAllNotificationsRead = async (): Promise<void> => {
  if (REAL_API_ENABLED) {
    await post('/notifications/read-all');
    return;
  }
  const db = loadDb();
  db.phase4.notifications.forEach((notification) => { notification.read = true; });
  saveDb(db);
};

export const getProfile = (seed?: ProfileSeed): Promise<ProfileInfo> =>
  MOCK_PROFILE ? mock.getProfile(seed) : notAvailable();

export const setProfilePhoto = (dataUrl: string | null): Promise<void> =>
  MOCK_PROFILE ? mock.setProfilePhoto(dataUrl) : notAvailable();

export const listServiceRequests = (): Promise<ServiceRequest[]> =>
  MOCK_PROFILE ? mock.listServiceRequests() : notAvailable();

export const createServiceRequest = (input: {
  kind: ServiceRequestKind;
  category: string;
  description: string;
}): Promise<ServiceRequest> =>
  MOCK_PROFILE ? mock.createServiceRequest(input) : notAvailable();

export const getNominees = (): Promise<Nominee[]> =>
  MOCK_PROFILE ? mock.getNominees() : notAvailable();

export const saveNominees = (nominees: Nominee[]): Promise<Nominee[]> =>
  MOCK_PROFILE ? mock.saveNominees(nominees) : notAvailable();

export interface CustomerNominee {
  id: string;
  name: string;
  relationship: string;
  dateOfBirth: string;
  guardianName: string;
  guardianRelationship: string;
  percentageShare: number;
  address: string;
  mobileNumber: string;
  minor: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CustomerNomineeInput = Omit<CustomerNominee, 'id' | 'createdAt' | 'updatedAt'>;

const customerNomineesPath = (customerId: string): string =>
  `/customers/${encodeURIComponent(customerId)}/nominees`;

export const listCustomerNominees = (customerId: string): Promise<CustomerNominee[]> =>
  REAL_API_ENABLED
    ? get<CustomerNominee[]>(customerNomineesPath(customerId))
    : notAvailable();

export const getCustomerNominee = (
  customerId: string,
  nomineeId: string,
): Promise<CustomerNominee> =>
  REAL_API_ENABLED
    ? get<CustomerNominee>(`${customerNomineesPath(customerId)}/${encodeURIComponent(nomineeId)}`)
    : notAvailable();

export const addCustomerNominee = (
  customerId: string,
  input: CustomerNomineeInput,
): Promise<CustomerNominee> =>
  REAL_API_ENABLED
    ? post<CustomerNominee>(customerNomineesPath(customerId), input)
    : notAvailable();

export const updateCustomerNominee = (
  customerId: string,
  nomineeId: string,
  input: CustomerNomineeInput,
): Promise<CustomerNominee> =>
  REAL_API_ENABLED
    ? put<CustomerNominee>(`${customerNomineesPath(customerId)}/${encodeURIComponent(nomineeId)}`, input)
    : notAvailable();

export const deleteCustomerNominee = (
  customerId: string,
  nomineeId: string,
): Promise<void> =>
  REAL_API_ENABLED
    ? del<void>(`${customerNomineesPath(customerId)}/${encodeURIComponent(nomineeId)}`)
    : notAvailable();

export const getSettings = (): Promise<ProfileSettings> =>
  MOCK_PROFILE ? mock.getSettings() : notAvailable();

export const saveSettings = (settings: ProfileSettings): Promise<void> =>
  MOCK_PROFILE ? mock.saveSettings(settings) : notAvailable();

export const validateChangeMpin = (currentMpin: string, newMpin: string): Promise<void> =>
  MOCK_PROFILE ? mock.validateChangeMpin(currentMpin, newMpin) : notAvailable();

export const validateChangePassword = (
  currentPassword: string,
  newPassword: string,
  seed?: ProfileSeed
): Promise<void> =>
  MOCK_PROFILE ? mock.validateChangePassword(currentPassword, newPassword, seed) : notAvailable();

export const requestProfileOtp = () =>
  MOCK_PROFILE ? mock.requestProfileOtp() : notAvailable();

export const resendProfileOtp = (authId: string) =>
  MOCK_PROFILE ? mock.resendProfileOtp(authId) : notAvailable();

export const verifyAndChangeMpin = (
  authId: string,
  newMpin: string,
  otp: string
): Promise<{ reference: string }> =>
  MOCK_PROFILE ? mock.verifyAndChangeMpin(authId, newMpin, otp) : notAvailable();

export const verifyAndChangePassword = (
  authId: string,
  newPassword: string,
  otp: string
): Promise<{ reference: string }> =>
  MOCK_PROFILE ? mock.verifyAndChangePassword(authId, newPassword, otp) : notAvailable();

export const deregister = (mpin: string): Promise<void> =>
  MOCK_PROFILE ? mock.deregister(mpin) : notAvailable();
